import _get from 'lodash/get';
import _set from 'lodash/set';
import isEqual from 'react-fast-compare';
import type { DependsOnType } from '@unifyapps/defs/types/page';
import { ENTIRE_ENTITY } from '../../../dependency/const';
import type { UseGetNoCodeComputeDataType } from '../useGetNoCodeComputedData';
import type { ComputeContextType } from '../BuildComputeContext';

export function getIsDependentContextChanged({
  prevContext,
  context,
  dependsOn,
  notifyingUpdateForId,
  getNoCodeComputedData,
}: {
  dependsOn: DependsOnType[];
  context: ComputeContextType;
  prevContext?: Record<string, unknown>;
  notifyingUpdateForId: string | null;
  getNoCodeComputedData: UseGetNoCodeComputeDataType['getNoCodeComputedData'];
}): {
  isContextChanged: boolean;
  nextContext: Record<string, unknown>;
} {
  // If there's no last context, we should always rerender
  if (!prevContext) {
    return {
      isContextChanged: true,
      nextContext: {},
    };
  }

  // Create a new context object to store updated values
  const nextContext = {};

  dependsOn.forEach((dependency) => {
    const { p, id } = dependency;
    const isDependentOnEntireEntity = p.find((path) => path === ENTIRE_ENTITY);
    // when we are dependent on the entire entity,
    // we compute and save it without picking a specific path
    if (isDependentOnEntireEntity) {
      nextContext[id] = getNoCodeComputedData<unknown>(context[id] as string, context);
    } else {
      p.forEach((path) => {
        const currentValue = _get(context, path);
        // TODO: perf: maybe we can create only one instance of createExpressionEvaluator as the context object stays the same
        const computedCurrentValue = getNoCodeComputedData<unknown>(
          currentValue as string,
          context,
        );
        _set(nextContext, path, computedCurrentValue);
      });
    }
  });

  const hasChanges =
    notifyingUpdateForId === null
      ? !isEqual(prevContext, nextContext)
      : !isEqual(prevContext[notifyingUpdateForId], nextContext[notifyingUpdateForId]);

  return {
    isContextChanged: hasChanges,
    nextContext,
  };
}
